import React, { useEffect, useState } from 'react';
import { styles } from './HeaderLayout.css';
import { stylesLocalised } from './HeaderLayoutLocalised.css';
import { HeaderUtilities } from './components/HeaderUtilities/HeaderUtilities';
import { HeaderUtilityButtonProps } from './components/HeaderUtilityButton/HeaderUtilityButton';
import { HeaderMenuButton } from './components/HeaderMenuButton/HeaderMenuButton';
import { HeaderJbLogo } from './components/HeaderJbLogo/HeaderJbLogo';
import { QuickSearch } from './components/QuickSearch/QuickSearch';
import { TrackingProvider, useTrackingContext } from '../Tracking';
import { HeaderNavigation, HeaderNavigationProps } from './components/HeaderNavigation/HeaderNavigation';
import { IdPrefixProvider } from '../IdPrefix';
import { classNames } from '@jbhi-fi/lanyard-ui/global/utils';
import { useBreakpoint } from '@jbhi-fi/lanyard-ui/utils/useBreakpoint';
import { CarouselMainSnackbars } from '../CarouselMainSnackbars/CarouselMainSnackbars';
import { CountDownSnackbar } from '../CountDownSnackbar/CountDownSnackbar';
import { SubSnackbar } from '../SubSnackbars/SubSnackbars';
import { GroupedSnackbarsProps } from '../Contentful/transformers/SnackbarTransformer';
import { CouponProgressBanner } from '../CouponProgressBanner';
import { Coupon, Location } from '../types';
import { getFriendlyFormattedDate } from '@jbhi-fi/utils/src/date/getFriendlyFormattedDate';
import type { SearchTooltipConfig } from '../SearchTooltip/useSearchTooltipConfig';

export type HeaderLayoutProps = {
  utilities: Array<HeaderUtilityButtonProps>;
  isLocalisationEnabled?: boolean;
  LocalisationHeader?: React.ReactElement;
  headerNavigationProps?: HeaderNavigationProps;
  snackbarProps?: GroupedSnackbarsProps;
  logoSubtitle?: string;
  couponOffering?: {
    coupon: Coupon;
    shouldShowSpendAndGetBanner: boolean;
    currentSpend: number;
    isProductExcluded: boolean;
    handleSelectCoupon: (couponCode: string, locationOverride?: Location) => void;
    handleUpdateShowSpendAndGetBanner: (shouldShowBanner: boolean) => void;
  };
  isCartPage?: boolean;
  searchPlaceholder?: string;
  searchTooltipConfig?: SearchTooltipConfig;
};

export const HeaderLayout = ({
  utilities,
  isLocalisationEnabled,
  LocalisationHeader,
  headerNavigationProps,
  snackbarProps,
  logoSubtitle,
  couponOffering,
  isCartPage,
  searchPlaceholder,
  searchTooltipConfig,
}: HeaderLayoutProps) => {
  const [isNavigationOpen, setIsNavigationOpen] = useState(false);
  const [isSnackbarVisible, setIsSnackbarVisible] = useState(false);
  const [isMenuExpanded, setIsMenuExpanded] = useState(false);

  const appliedStyles = isLocalisationEnabled ? stylesLocalised : styles;
  const { tracker } = useTrackingContext();
  const isDesktop = useBreakpoint('md', 'up');
  const isMobile = useBreakpoint('md', 'down');

  const shouldLockScrollMobile = isNavigationOpen;
  const shouldLockScrollDesktop = isDesktop && isMenuExpanded;

  const shouldLockScroll = shouldLockScrollMobile || shouldLockScrollDesktop;

  useEffect(() => {
    if (typeof document === 'undefined') return;
    document.body.classList.toggle('header-nav-open', shouldLockScroll);
    document.documentElement.classList.toggle('header-nav-open', shouldLockScroll);

    return () => {
      document.body.classList.remove('header-nav-open');
      document.documentElement.classList.remove('header-nav-open');
    };
  }, [shouldLockScroll]);

  const handleToggleNavigation = () => {
    tracker.trackCustomEvent({
      event: `custom.menu.${isNavigationOpen ? 'close' : 'open'}`,
      group: tracker.group,
      type: isNavigationOpen ? 'close' : 'open',
    });
    setIsNavigationOpen(!isNavigationOpen);
  };

  const { main = [], sub = [], countdown = [] } = snackbarProps || {};

  return (
    <TrackingProvider
      prefix="custom"
      context="menu"
      group={isDesktop ? 'Mega Nav' : 'mobile-menu'}
      trackPageLoad={false}
    >
      <div className={classNames(appliedStyles.container, styles.wrapper({ isNavigationOpen }))}>
        {main?.length ? <CarouselMainSnackbars items={main} setIsSnackbarVisible={setIsSnackbarVisible} /> : null}
        <div className={appliedStyles.contentContainer} id="header-content-container">
          <IdPrefixProvider prefix="header">
            <HeaderMenuButton isOpen={isNavigationOpen} onClick={handleToggleNavigation} />
            <HeaderJbLogo subtitle={logoSubtitle} />
            <QuickSearch
              placeholder={searchPlaceholder || 'Search products, brands, and more...'}
              searchTooltipConfig={searchTooltipConfig}
              className={styles.quickSearch}
            />
            {isLocalisationEnabled && LocalisationHeader ? LocalisationHeader : null}
            <HeaderUtilities utilities={utilities} />
          </IdPrefixProvider>
        </div>
        {headerNavigationProps ? (
          <div className={styles.navigation({ isOpen: isNavigationOpen })} id="header-nav-container">
            <HeaderNavigation
              {...headerNavigationProps}
              isSnackbarVisible={isSnackbarVisible}
              onMenuOpenChange={setIsMenuExpanded}
            />
          </div>
        ) : null}
      </div>
      {sub?.length ? (
        <div id="snackbar-sub" data-testid="snackbar-sub">
          <SubSnackbar subSnackbars={sub} />{' '}
        </div>
      ) : null}

      {countdown.length ? (
        <div id="snackbar-countdown" data-testid="snackbar-countdown">
          <CountDownSnackbar {...countdown[0]} />
        </div>
      ) : null}
      {(!isCartPage || isMobile) && couponOffering?.coupon && couponOffering?.shouldShowSpendAndGetBanner ? (
        <CouponProgressBanner
          variant={isCartPage ? 'mini' : 'default'}
          title={couponOffering.coupon.content.displayName}
          expiryDate={getFriendlyFormattedDate(couponOffering.coupon.expiryDate, 'extraFriendly', false)}
          onApplyCoupon={() => couponOffering.handleSelectCoupon(couponOffering.coupon.couponCode, 'banner')}
          onClose={() => couponOffering.handleUpdateShowSpendAndGetBanner(false)}
          isApplied={couponOffering.coupon.isApplied}
          discount={couponOffering.coupon.discount}
          targetValue={couponOffering.coupon.minimumSpend}
          value={couponOffering.currentSpend}
          hasWarning={couponOffering.isProductExcluded}
        />
      ) : null}
    </TrackingProvider>
  );
};
